import { z } from "zod";

/** Inpatient / HIS Phase 1 — wards, beds, admissions (ADT). */

export const wardKindSchema = z.enum([
	"general",
	"icu",
	"ccu",
	"pediatric",
	"maternity",
	"surgical",
	"isolation",
	"other",
]);
export type WardKind = z.infer<typeof wardKindSchema>;

export const bedStatusSchema = z.enum(["available", "maintenance"]);
export type BedStatus = z.infer<typeof bedStatusSchema>;

export const admissionStatusSchema = z.enum(["active", "discharged"]);
export type AdmissionStatus = z.infer<typeof admissionStatusSchema>;

// ===== Ward =====
export const wardApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	name: z.string(),
	kind: z.string().nullable(),
	notes: z.string().nullable(),
	created_at: z.string().datetime(),
	/** Computed: bed counts for the board. */
	bed_count: z.number().int().optional(),
	occupied_count: z.number().int().optional(),
});
export type WardApi = z.infer<typeof wardApiSchema>;

export const createWardInputSchema = z.object({
	name: z.string().min(1).max(120),
	kind: wardKindSchema.nullable().optional(),
	notes: z.string().max(1000).nullable().optional(),
});
export type CreateWardInput = z.infer<typeof createWardInputSchema>;

// ===== Bed =====
export const bedApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	ward_id: z.string().uuid(),
	label: z.string(),
	status: z.string(),
	notes: z.string().nullable(),
	created_at: z.string().datetime(),
	/** Computed: the active admission occupying this bed, if any. */
	occupant: z
		.object({
			admission_id: z.string().uuid(),
			patient_id: z.string().uuid(),
			patient_name: z.string(),
			admit_at: z.string().datetime(),
		})
		.nullable()
		.optional(),
});
export type BedApi = z.infer<typeof bedApiSchema>;

export const createBedInputSchema = z.object({
	ward_id: z.string().uuid(),
	label: z.string().min(1).max(60),
	notes: z.string().max(500).nullable().optional(),
});
export type CreateBedInput = z.infer<typeof createBedInputSchema>;

export const updateBedStatusInputSchema = z.object({
	status: bedStatusSchema,
});
export type UpdateBedStatusInput = z.infer<typeof updateBedStatusInputSchema>;

// ===== Admission =====
export const admissionApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	ward_id: z.string().uuid().nullable(),
	bed_id: z.string().uuid().nullable(),
	attending_doctor_id: z.string().uuid().nullable(),
	admitted_by_member_id: z.string().uuid().nullable(),
	admission_number: z.string(),
	status: z.string(),
	admit_at: z.string().datetime(),
	discharge_at: z.string().datetime().nullable(),
	reason: z.string().nullable(),
	admit_diagnosis: z.string().nullable(),
	discharge_summary: z.string().nullable(),
	created_at: z.string().datetime(),
	// Computed joins for the list/board.
	patient_name: z.string().optional(),
	ward_name: z.string().nullable().optional(),
	bed_label: z.string().nullable().optional(),
});
export type AdmissionApi = z.infer<typeof admissionApiSchema>;

export const admitPatientInputSchema = z.object({
	patient_id: z.string().uuid(),
	ward_id: z.string().uuid().nullable().optional(),
	bed_id: z.string().uuid().nullable().optional(),
	attending_doctor_id: z.string().uuid().nullable().optional(),
	reason: z.string().max(1000).nullable().optional(),
	admit_diagnosis: z.string().max(1000).nullable().optional(),
});
export type AdmitPatientInput = z.infer<typeof admitPatientInputSchema>;

export const transferAdmissionInputSchema = z.object({
	ward_id: z.string().uuid().nullable().optional(),
	bed_id: z.string().uuid().nullable().optional(),
});
export type TransferAdmissionInput = z.infer<
	typeof transferAdmissionInputSchema
>;

export const dischargeAdmissionInputSchema = z.object({
	discharge_summary: z.string().max(4000).nullable().optional(),
});
export type DischargeAdmissionInput = z.infer<
	typeof dischargeAdmissionInputSchema
>;
